import { type ShareProfileButton_userProfile$key } from '#app/__generated__/relay/ShareProfileButton_userProfile.graphql.js';
import { Button, type ButtonOwnProps } from '#app/components/Buttons/Button.js';
import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCopyProfileUrl } from '#app/hooks/useCopyProfileUrl.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { ShareAltIcon } from '@contra/icons/icons';
import { useMount } from '@contra/react-hooks/useMount';
import { graphql, useFragment } from '@contra/relay';
import { useCallback } from 'react';

const section = 'profile_share_button';

type ShareProfileButtonProps =
  | {
      buttonShape: 'circular';
      size?: ButtonOwnProps['size'] | number;
      userProfileRef: ShareProfileButton_userProfile$key;
      variant?: ButtonOwnProps['variant'];
    }
  | {
      buttonShape: 'default';
      size?: ButtonOwnProps['size'];
      userProfileRef: ShareProfileButton_userProfile$key;
      variant?: ButtonOwnProps['variant'];
    };

export const ShareProfileButton = ({
  buttonShape,
  size = 'small',
  userProfileRef,
  variant = 'V2TertiaryOutline',
}: ShareProfileButtonProps) => {
  const profile = useFragment<ShareProfileButton_userProfile$key>(
    graphql`
      fragment ShareProfileButton_userProfile on UserProfile {
        analyticsId
        visitorCanEdit
        displayUsername
      }
    `,
    userProfileRef,
  );
  const { analyticsId, displayUsername } = profile;

  const [urlSearchParameters, setUrlParameters] =
    useURLParameters('/@username');
  const shareProfileRouteParameter = urlSearchParameters.shareProfile;

  const isMobile = useIsMobile();

  const { track } = useAnalytics();

  const { onCopyProfileLink } = useCopyProfileUrl({
    userProfile: {
      displayUsername,
    },
    utmTag: {
      utm_campaign: 'social_sharing',
      utm_medium: 'independent_share',
      utm_source: 'copy_link',
    },
  });

  const handleCopyProfileLink = useCallback(() => {
    onCopyProfileLink();
    track('button', {
      action: 'tapped',
      button_name: 'social_share_copy_link',
      owner_id: analyticsId,
      section,
    });
  }, [analyticsId, onCopyProfileLink, track]);

  useMount(() => {
    if (shareProfileRouteParameter && (buttonShape === 'default' || isMobile)) {
      handleCopyProfileLink();

      setUrlParameters({
        shareProfile: undefined,
      });
    }
  });

  if (buttonShape === 'circular') {
    return (
      <CircularIconButton
        aria-label="Copy profile link to clipboard"
        icon={ShareAltIcon}
        onClick={handleCopyProfileLink}
        size={size}
        variant={variant}
      />
    );
  }

  return (
    <Button
      aria-label="Copy profile link to clipboard"
      icon={ShareAltIcon}
      onClick={handleCopyProfileLink}
      size={typeof size === 'number' ? 'small' : size}
      variant={variant}
    >
      Share profile
    </Button>
  );
};
